import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, ImageOff } from 'lucide-react';
import type { Me } from '../api/auth';
import {
  listEventPhotos,
  listPhotoFeed,
  PhotosDisabledError,
  removeEventPhoto,
} from '../api/eventPhotos';
import type { EventPhoto, GalleryEvent } from '../api/eventPhotos';
import { eventColorVar } from '../brand/eventColors';
import { formatInZone } from '../lib/datetime';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { EmptyState } from '../ui/EmptyState';
import { PhotoLightbox } from './PhotoLightbox';
import { PhotoUpload } from './PhotoUpload';

/**
 * The photo feed — TROOP-119.
 *
 * **The event still owns the photographs; this is a second way of reaching them.** There is no
 * unit-level album in the model and this does not invent one. Every block is headed by the event
 * that owns its pictures, links to that event, and takes uploads for it — what changes is the
 * ordering. The calendar files events by what is next; this files them by what just happened and
 * walks backwards, which is the only question a photograph ever answers.
 *
 * **So it pages backwards, not by month.** The conversations page walks a month at a time because
 * a month is how you look for a particular meeting. Nobody looks for a particular photograph that
 * way — you scroll until you recognise the trip — so the walk is a keyset cursor and the next page
 * arrives as you reach the bottom, with an explicit control behind it for anyone not scrolling.
 *
 * **The block heading is sticky, and that is the one piece of behaviour this screen spends.** A
 * contact sheet of twelve photographs is taller than a phone, so without it you are two swipes from
 * a picture with nothing on screen saying whose weekend it is. Pinning the plate makes the model's
 * one true fact — the event owns the photographs — something you cannot scroll away from.
 *
 * **Both doors, one screen** (rule 13b). `unitId` present is a member's; absent is a guardian's,
 * who holds no membership and would be 404'd by `UnitScopeMiddleware` on any `/units/` route. The
 * rows carry a `unitId` of their own and it is deliberately *not* used to build request paths —
 * only to caption a parent's feed, which spans every unit their children are in.
 *
 * **A tile is 96px and the gap is 2px**, tighter than the event page's grid on purpose. This is a
 * contact sheet rather than a list of items: at this density a campout's strip reads as one
 * object, which is what makes the heading above it worth pinning.
 */

/**
 * The height a row of the contact sheet aims for. Only ever a floor: a row that has to stretch to
 * reach the far edge gets taller, which is what keeps every photograph in it drawn at its own
 * shape rather than cropped to a common box.
 *
 * The arithmetic, because it is not obvious from the CSS. Each photograph takes
 * `flex-basis: ratio * ROW_HEIGHT` and `flex-grow: ratio`, so the free space on a row is shared out
 * in proportion to width — which means every item's *width / ratio* comes to the same number, and
 * that number is the row's height. Equal heights, ragged widths, both edges met, no measuring.
 */
const ROW_HEIGHT = 120;

/** Enough to soak up the slack on a last row of any width. See where they are rendered. */
const SPACERS = 8;

/**
 * A photograph's shape, or 4:3 when the row does not carry one.
 *
 * `width` and `height` are nullable: they are read at ingest, and a row written before that
 * existed has neither. Guessing landscape is the safe default — every phone camera and every
 * `<input type="file">` on a laptop produces one, and the picture is `object-fit: cover` regardless,
 * so a wrong guess costs a crop rather than a broken row.
 */
function aspectRatioOf(photo: EventPhoto): number {
  if (!photo.width || !photo.height) return 4 / 3;

  // Clamped, so one accidental panorama cannot claim an entire row on its own and one very tall
  // portrait cannot shrink to a sliver.
  return Math.min(Math.max(photo.width / photo.height, 0.6), 2.2);
}

export function PhotosScreen({
  unitId,
  unitName,
  me,
  onNavigate,
}: {
  /** Undefined on `/family` — that absence is what selects the guardian's door. */
  unitId?: string;
  /** The unit whose feed this is, or undefined for a parent's, which spans several. */
  unitName?: string;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const [events, setEvents] = useState<GalleryEvent[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [disabledReason, setDisabledReason] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Events whose unit narrowed who may add while this page was open, and what the server said
  // (TROOP-225). Keyed by event because the feed spans units and only some of them will have.
  //
  // It has to be held here rather than re-read: `refreshEvent` reads the *gallery*, which carries
  // photographs and no capability at all, so a refetch would leave `canAdd` exactly as stale as it
  // was and the control still drawn over a refusal. A short-lived override, cleared whenever the
  // feed itself is re-read.
  const [refused, setRefused] = useState<Record<string, string>>({});
  const [loadingMore, setLoadingMore] = useState(false);

  // Which event's strip is open, and where in it. Kept as a pair rather than as a flattened index:
  // the lightbox pages within one event, because "next photo" across a boundary would mean the
  // picture after the last of Saturday is the first of a campout three weeks earlier.
  const [viewing, setViewing] = useState<{ eventId: string; index: number } | null>(null);
  const [busy, setBusy] = useState(false);

  // One refresh per mount, so a genuinely broken image cannot spin the endpoint.
  const refreshed = useRef(false);
  const sentinel = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      setDisabledReason(null);
      const page = await listPhotoFeed(unitId);
      setEvents(page.events);
      setCursor(page.nextCursor);
      // The rows that just arrived carry a fresh `canAdd`, so the overrides have nothing left to
      // override. Cleared here and only here -- this is the one read that answers the question.
      setRefused({});
      setError(null);
    } catch (e) {
      if (e instanceof PhotosDisabledError) {
        setDisabledReason(e.message);
        setEvents([]);
        return;
      }
      setError(e instanceof Error ? e.message : 'The photos could not be loaded.');
    }
  }, [unitId]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadOlder = useCallback(async () => {
    if (!cursor || loadingMore) return;

    setLoadingMore(true);
    try {
      const page = await listPhotoFeed(unitId, cursor);
      setEvents((current) => [...(current ?? []), ...page.events]);
      setCursor(page.nextCursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load older photos.');
    } finally {
      setLoadingMore(false);
    }
  }, [unitId, cursor, loadingMore]);

  /**
   * Reaching the bottom loads the next page.
   *
   * `IntersectionObserver` rather than a scroll handler, and the button below it stays rendered
   * rather than being replaced by this: an observer needs the page to paint, and a background tab
   * does not — which is also what makes the control the only reliable path under browser
   * automation. Two ways to the same call, one of which cannot silently stop working.
   */
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !cursor) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void loadOlder();
      },
      // Started early, so the next page is usually there by the time the reader arrives.
      { rootMargin: '600px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [cursor, loadOlder]);

  /**
   * One event's block, re-read after an upload or a takedown.
   *
   * Deliberately not a reload of the whole feed: that would drop every page walked so far and
   * return the reader to the top, which after adding a photograph to something from last spring is
   * the opposite of what they asked for. The event's own gallery is the read, bounded here to the
   * strip length the feed uses.
   */
  const refreshEvent = useCallback(
    async (eventId: string) => {
      try {
        const photos = await listEventPhotos(unitId, eventId);
        if (!photos) return;

        setEvents((current) =>
          (current ?? []).map((event) =>
            event.eventId === eventId
              ? { ...event, photoCount: photos.length, photos: photos.slice(0, STRIP) }
              : event,
          ),
        );
      } catch (e) {
        if (e instanceof PhotosDisabledError) setDisabledReason(e.message);
      }
    },
    [unitId],
  );

  async function remove(eventId: string, photo: EventPhoto) {
    setBusy(true);
    setError(null);
    try {
      await removeEventPhoto(unitId, eventId, photo.id);
      setViewing(null);
      await refreshEvent(eventId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That photo could not be removed.');
    } finally {
      setBusy(false);
    }
  }

  const open = viewing ? events?.find((e) => e.eventId === viewing.eventId) : undefined;

  return (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              {unitName ?? 'Your family'}
            </p>
            <h1
              className="t-heading"
              style={{
                color: 'var(--color-text-primary)',
                margin: 'var(--space-2) 0 var(--space-2)',
              }}
            >
              Photos
            </h1>
          </div>
        </Gutter>
      </section>

      <Gutter>
        <div style={{ paddingBlock: 'var(--space-6)' }}>
          {error && (
            <p className="t-body" role="alert" style={{ color: 'var(--color-status-blocked)' }}>
              {error}
            </p>
          )}

          {disabledReason && (
            // No control anywhere on the page, and a sentence naming the decision. §6: a disabled
            // control owes a reason, and this one is a leadership call the viewer cannot act on.
            <p className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
              {disabledReason} A unit admin can turn them back on in the unit&rsquo;s settings.
            </p>
          )}

          {events === null && !error && (
            <p className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
              Loading&hellip;
            </p>
          )}

          {events !== null && events.length === 0 && !disabledReason && (
            <EmptyState
              icon={ImageOff}
              headline="No photos yet"
              // No claim about who may add: this feed spans every unit the reader touches
              // (TROOP-225) and they can have chosen differently, so there is no one true sentence
              // to write here. The event's own gallery names its unit's answer, which is also the
              // screen somebody is on when they go to add.
              explanation="Photos added to an event show up here, newest event first."
            />
          )}

          {events !== null && events.length > 0 && (
            <div className="flex flex-col" style={{ gap: 'var(--space-8)' }}>
              {events.map((event) => (
                <EventBlock
                  key={event.eventId}
                  event={event}
                  // The door, not the row's unit. See the component remarks.
                  unitId={unitId}
                  showUnit={unitId === undefined}
                  onOpenEvent={() =>
                    onNavigate(
                      unitId
                        ? `/units/${unitId}/events/${event.eventId}`
                        : `/family/events/${event.eventId}`,
                    )
                  }
                  onView={(index) => setViewing({ eventId: event.eventId, index })}
                  onAdded={() => refreshEvent(event.eventId)}
                  onDisabled={setDisabledReason}
                  // The unit narrowed who may add while a sheet was open (TROOP-225). The row's
                  // own `canAdd` is now stale and cannot be refreshed on its own -- the gallery
                  // read carries photographs and no capability -- so the answer is held here and
                  // the section swaps the control for what the server said. Dropping the message
                  // and refetching the strip is the version that closes the sheet, changes
                  // nothing, and leaves the button sitting over a refusal.
                  onRefused={(message) =>
                    setRefused((current) => ({ ...current, [event.eventId]: message }))
                  }
                  refusedMessage={refused[event.eventId]}
                  onImageError={() => {
                    // A signed URL that has expired, most likely. One re-list per mount turns a
                    // page left open overnight into a refresh rather than a wall of broken images.
                    if (refreshed.current) return;
                    refreshed.current = true;
                    void load();
                  }}
                />
              ))}

              {/* Both paths to the same call. The sentinel loads it on approach; the button is
                  what a keyboard reaches and what works in a tab that never paints. */}
              <div ref={sentinel} />

              {cursor !== null && (
                <div>
                  <Button variant="secondary" onClick={() => void loadOlder()}>
                    {loadingMore ? 'Loading…' : 'Load older photos'}
                  </Button>
                </div>
              )}

              {cursor === null && (
                <p
                  className="t-caption"
                  style={{ color: 'var(--color-text-muted)', margin: 0, textAlign: 'center' }}
                >
                  That&rsquo;s everything.
                </p>
              )}
            </div>
          )}
        </div>
      </Gutter>

      {open && viewing && (
        <PhotoLightbox
          photos={open.photos}
          index={viewing.index}
          zone={open.timeZoneId}
          busy={busy}
          onIndex={(index) => setViewing({ eventId: open.eventId, index })}
          onClose={() => setViewing(null)}
          onRemove={(photo) => void remove(open.eventId, photo)}
        />
      )}

      <Footer />
    </Page>
  );
}

/** How many the feed draws under one heading. The server's bound; kept here to splice a refresh. */
const STRIP = 12;

/**
 * One event, and the contact sheet beneath it.
 *
 * The plate carries the date, the kind and the name, and nothing else — everything else about the
 * event is one tap away on the event's own page, which is where it belongs. The kind's swatch is a
 * left rule, the same device the calendar uses so a kind reads the same on both screens, and the
 * kind's name is text beside it: colour is never the only signal.
 */
function EventBlock({
  event,
  unitId,
  showUnit,
  onOpenEvent,
  onView,
  onAdded,
  onDisabled,
  onRefused,
  refusedMessage,
  onImageError,
}: {
  event: GalleryEvent;
  unitId?: string;
  showUnit: boolean;
  onOpenEvent: () => void;
  onView: (index: number) => void;
  onAdded: () => void | Promise<void>;
  onDisabled: (message: string) => void;
  onRefused: (message: string) => void;
  /** Set once this unit has refused an upload from this reader mid-flight; the server's words. */
  refusedMessage?: string;
  onImageError: () => void;
}) {
  const cancelled = event.status === 'Cancelled';
  const more = event.photoCount - event.photos.length;

  return (
    <section className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
      <div
        style={{
          // The signature of this screen. See the screen's remarks: a contact sheet is taller than
          // a phone, and the heading is what says whose weekend you are looking at.
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: 'var(--color-surface-page)',
          borderInlineStart: `3px solid ${eventColorVar(event.eventTypeColorKey)}`,
          paddingInlineStart: 'var(--space-3)',
          paddingBlock: 'var(--space-2)',
        }}
      >
        <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
          {/* The year is on every row, unlike everywhere else in the product. This screen's whole
              job is going backwards, so "Sat, Aug 28" three screens down is ambiguous in exactly
              the way a calendar's never is. No time: what hour a campout started is not what a
              photograph of it is filed under. */}
          {formatInZone(event.startsAt, event.timeZoneId, {
            year: 'numeric',
            hour: undefined,
            minute: undefined,
          })}
          {event.eventTypeName && <> &middot; {event.eventTypeName}</>}
          {showUnit && <> &middot; {event.unitName}</>}
        </p>

        <button
          type="button"
          onClick={onOpenEvent}
          className="inline-flex items-center gap-2 text-left"
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            marginBlockStart: 'var(--space-1)',
            font: 'var(--text-subheading)',
            color: 'var(--color-text-primary)',
            cursor: 'pointer',
          }}
        >
          <span>{event.title}</span>
          <ArrowRight size={16} aria-hidden="true" style={{ color: 'var(--color-text-muted)' }} />
        </button>

        {cancelled && (
          // On the plate rather than behind a tap: a family scrolling past a cancelled trip's
          // photographs should not have to open the event to find out it was called off.
          <p
            className="t-caption"
            style={{ color: 'var(--color-status-blocked)', margin: 'var(--space-1) 0 0' }}
          >
            Cancelled{event.cancellationReason ? ` — ${event.cancellationReason}` : ''}
          </p>
        )}
      </div>

      <ul
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          // Rows size to their own content rather than stretching to the tallest line, which is
          // what lets a short last row stay short.
          alignItems: 'flex-start',
          // 2px, not a space token. The tokens start at 4px and this is a contact sheet: the
          // pictures should read as one sheet of film rather than as a row of cards.
          gap: '2px',
          listStyle: 'none',
          padding: 0,
          margin: 0,
          borderRadius: 'var(--radius-sm)',
          overflow: 'hidden',
        }}
      >
        {event.photos.map((photo, index) => {
          const ratio = aspectRatioOf(photo);

          return (
            <li
              key={photo.id}
              style={{
                // A justified row: every photograph takes the width its own shape asks for and the
                // row reaches both edges, with the height falling out of the arithmetic above. The
                // alternative -- a square grid -- centre-crops a landscape picture, and leaves a
                // two-photo Monday looking like a broken forty-photo campout.
                flexBasis: ratio * ROW_HEIGHT,
                flexGrow: ratio,
                minWidth: 0,
                // Height from the width the row settled on, so nothing here is cropped by the
                // layout. `object-fit: cover` below only ever trims the clamp in aspectRatioOf.
                aspectRatio: String(ratio),
              }}
            >
              <button
                type="button"
                onClick={() => onView(index)}
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  padding: 0,
                  // No border. A rule around a photograph is fussy at this size and fights the
                  // picture; at this density the sheet's own edge is the only one that reads.
                  border: 'none',
                  background: 'var(--color-surface-sunken)',
                  cursor: 'pointer',
                }}
              >
                <img
                  src={photo.thumbnailUrl ?? photo.url}
                  alt={photo.caption ?? `Photo added by ${photo.addedByDisplayName}`}
                  loading="lazy"
                  onError={onImageError}
                  style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </button>
            </li>
          );
        })}

        {/* What stops the last row stretching. A justified layout grows every item to fill its
            row, which on a row holding one photograph blows it up to the full column width -- so
            the tail absorbs the slack instead. Zero height, so they take no space of their own. */}
        {Array.from({ length: SPACERS }, (_, i) => (
          <li key={`spacer-${i}`} aria-hidden="true" style={{ flexGrow: 999, height: 0 }} />
        ))}
      </ul>

      <div className="flex flex-wrap items-center" style={{ gap: 'var(--space-3)' }}>
        {/* Absent rather than disabled when the unit does not let this reader add (TROOP-225),
            and without a sentence: that sentence is the same for every section of a unit's feed,
            so repeating it under eight events would be eight copies of one fact. The gallery is
            where somebody goes to add and it says it there. A parent's feed spans units, which is
            why `canAdd` is read per row. */}
        {event.canAdd && !refusedMessage && (
          <PhotoUpload
            unitId={unitId}
            eventId={event.eventId}
            // Named here and not on the event's own page: several events are on screen at once, and
            // the sheet is the last chance to notice you tapped the wrong weekend.
            eventTitle={event.title}
            onAdded={onAdded}
            onDisabled={onDisabled}
            onRefused={onRefused}
          />
        )}

        {/* The exception to the paragraph above. Somebody who tapped and was refused is owed the
            reason on the spot -- a control that quietly stops working is the silent failure this
            whole ticket is written against -- and it renders under one section rather than all of
            them because only that reader's tap produced it. */}
        {refusedMessage && (
          <p
            className="t-caption"
            role="status"
            style={{ color: 'var(--color-text-secondary)', margin: 0 }}
          >
            {refusedMessage}
          </p>
        )}

        {more > 0 && (
          <button
            type="button"
            onClick={onOpenEvent}
            className="t-caption"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-link)',
              padding: 'var(--space-2) 0',
              minHeight: 'var(--tap-min)',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            See all {event.photoCount} on the event
          </button>
        )}
      </div>
    </section>
  );
}
